import { Component, useMemo, useState } from 'react';
import CalculatorForm from './components/CalculatorForm.jsx';
import FeeBreakdown from './components/FeeBreakdown.jsx';
import ProductImportTool from './components/ProductImportTool.jsx';
import ResultCard from './components/ResultCard.jsx';
import RuleVersionBadge from './components/RuleVersionBadge.jsx';
import feeRules from './data/feeRules.json';
import { calculateFees } from './lib/feeEngine.js';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-stone-50 px-4">
          <div className="w-full max-w-md rounded-lg border border-rose-200 bg-white p-6 shadow-lg">
            <h1 className="text-xl font-bold text-rose-700">Terjadi Kesalahan</h1>
            <p className="mt-2 text-sm text-slate-600">
              Aplikasi mengalami error yang tidak terduga. Silakan refresh halaman untuk mencoba lagi.
            </p>
            {this.state.error && (
              <details className="mt-4">
                <summary className="cursor-pointer text-sm font-semibold text-slate-700">Detail Error</summary>
                <pre className="mt-2 overflow-auto rounded bg-slate-100 p-2 text-xs text-slate-800">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
            <button
              onClick={() => window.location.reload()}
              className="mt-4 w-full rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-800"
            >
              Refresh Halaman
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const initialForm = {
  marketplace: 'shopee',
  productName: '',
  sku: '',
  category: 'general',
  sellingPrice: 100000,
  quantity: 1,
  costOfGoods: 60000,
  sellerDiscount: 0,
  sellerVoucher: 0,
  packingCost: 3000,
  targetProfit: 20000,
  affiliateRate: 0,
  adsRate: 0,
  freeShippingXtra: false,
  promoXtra: false,
  preOrder: false,
  platformCampaign: false,
};

export default function App() {
  const [formData, setFormData] = useState(initialForm);

  const result = useMemo(() => calculateFees(formData, feeRules), [formData]);

  const updateField = (field, value) => {
    setFormData((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const resetForm = () => {
    setFormData(initialForm);
  };

  const loadImportedProduct = (productInput) => {
    setFormData({
      ...initialForm,
      ...productInput,
    });
  };

  return (
    <ErrorBoundary>
      <main className="min-h-screen bg-stone-50 text-slate-950">
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 px-4 py-6 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-emerald-700">
              Marketplace Fee Calculator
            </p>
            <h1 className="mt-2 text-3xl font-bold tracking-normal text-slate-950 sm:text-4xl">
              Kalkulator Fee Shopee & TikTok Shop
            </h1>
          </div>
          <RuleVersionBadge rules={feeRules} />
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-6 px-4 py-6 sm:px-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(360px,0.95fr)] lg:px-8">
        <CalculatorForm
          formData={formData}
          onFieldChange={updateField}
          onReset={resetForm}
          rules={feeRules}
        />

        <div className="space-y-6">
          <ResultCard result={result} />
          <FeeBreakdown result={result} />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-8 sm:px-6 lg:px-8">
        <ProductImportTool rules={feeRules} onLoadProduct={loadImportedProduct} />
      </section>
    </main>
    </ErrorBoundary>
  );
}
